import {
  ActionIcon,
  Alert,
  Autocomplete,
  Button,
  Grid,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,

} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { Decimal } from 'decimal.js';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { useT } from '../i18n/useT.js';
import { formatMoney, parseMoney, toIsoDate } from '../format.js';
import { uuid } from '../uuid.js';
import { useAccounts, useCreateJournalEntry } from '../api/hooks.js';
import type { CreateJournalEntry, JournalLineInput } from '../api/hooks.js';

/**
 * Posting a journal entry.
 *
 * The whole design is built around one rule: **debits must equal credits**, and
 * a posting is immutable once made. So the running total is shown continuously
 * rather than revealed on submit, and the button stays disabled while the entry
 * is out of balance — the API would refuse it with 422 anyway, and finding that
 * out after pressing Post is a worse way to learn it.
 *
 * A line takes a debit **or** a credit, never both: the database enforces
 * exactly one strictly positive side, so offering two fields that can both be
 * filled would invite a refusal the form could have prevented.
 */

interface DraftLine {
  key: string;
  accountCode: string;
  /** Free text as typed; parsed with parseMoney, which handles both separators. */
  debit: string;
  credit: string;
  description: string;
}

function emptyLine(): DraftLine {
  return {
    key: uuid(),
    accountCode: '',
    debit: '',
    credit: '',
    description: '',
  };
}

export interface JournalEntryFormProps {
  readonly onPosted?: (() => void) | undefined;
}

export function JournalEntryForm({ onPosted }: JournalEntryFormProps): ReactNode {
  const t = useT();
  const accounts = useAccounts();
  const create = useCreateJournalEntry();

  const [date, setDate] = useState<Date | null>(new Date());
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(), emptyLine()]);

  const accountOptions = useMemo(
    () =>
      (accounts.data?.items ?? [])
        .filter((account) => account.isActive)
        .map((account) => ({
          value: account.code,
          label: `${account.code} — ${account.name}`,
        })),
    [accounts.data],
  );

  const totals = useMemo(() => {
    let debit = new Decimal(0);
    let credit = new Decimal(0);
    for (const line of lines) {
      debit = debit.plus(parseMoney(line.debit) ?? 0);
      credit = credit.plus(parseMoney(line.credit) ?? 0);
    }
    return { debit, credit, difference: debit.minus(credit) };
  }, [lines]);

  const filledLines = lines.filter(
    (line) =>
      line.accountCode.trim() !== '' &&
      (parseMoney(line.debit) !== null || parseMoney(line.credit) !== null),
  );

  const bothSides = lines.some(
    (line) => parseMoney(line.debit) !== null && parseMoney(line.credit) !== null,
  );

  const balanced = totals.difference.isZero() && !totals.debit.isZero();
  const canPost =
    balanced &&
    !bothSides &&
    filledLines.length >= 2 &&
    description.trim() !== '' &&
    date !== null &&
    !create.isPending;

  function updateLine(key: string, patch: Partial<DraftLine>): void {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );
  }

  function handleSubmit(): void {
    if (date === null) {
      return;
    }
    const body: CreateJournalEntry = {
      date: toIsoDate(date),
      description: description.trim(),
      ...(reference.trim() === '' ? {} : { reference: reference.trim() }),
      lines: filledLines.map((line): JournalLineInput => {
        const debit = parseMoney(line.debit);
        const credit = parseMoney(line.credit);
        return {
          accountCode: line.accountCode.trim(),
          ...(debit === null ? {} : { debit: debit.toFixed(2) }),
          ...(credit === null ? {} : { credit: credit.toFixed(2) }),
          ...(line.description.trim() === ''
            ? {}
            : { description: line.description.trim() }),
        };
      }),
    };

    create.mutate(body, {
      onSuccess: (entry) => {
        // The entry is already in the ledger by the time this runs. A
        // confirmation message must therefore never throw: crashing here would
        // report a failure for a posting that actually succeeded, and the user
        // would post it again. So the amount is read defensively rather than
        // trusted to be present.
        const posted = entry.totals?.debit ?? totals.debit.toFixed(2);
        notifications.show({
          color: 'green',
          title: t('entryForm.posted'),
          message: `${entry.description ?? description.trim()} — ${formatMoney(posted)} kr.`,
        });
        setDescription('');
        setReference('');
        setLines([emptyLine(), emptyLine()]);
        onPosted?.();
      },
    });
  }

  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <DateInput
              label={t('entryForm.date')}
              value={date}
              onChange={(value) => {
                setDate(value === null ? null : new Date(value));
              }}
              valueFormat="DD.MM.YYYY"
              required
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <TextInput
              // "Posteringstekst", not "Tekst": each line has its own text, and
              // two fields called the same thing on one form is a question the
              // user should not have to answer.
              label={t('entryForm.description')}
              value={description}
              onChange={(event) => {
                setDescription(event.currentTarget.value);
              }}
              required
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <TextInput
              label={t('entryForm.reference')}
              value={reference}
              onChange={(event) => {
                setReference(event.currentTarget.value);
              }}
            />
          </Grid.Col>
        </Grid>

        <Table.ScrollContainer minWidth={720}>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: 220 }}>{t('journal.col.account')}</Table.Th>
                <Table.Th>{t('journal.col.text')}</Table.Th>
                <Table.Th style={{ width: 140, textAlign: 'right' }}>
                  {t('journal.col.debit')}
                </Table.Th>
                <Table.Th style={{ width: 140, textAlign: 'right' }}>
                  {t('journal.col.credit')}
                </Table.Th>
                <Table.Th style={{ width: 44 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map((line, index) => (
                <Table.Tr key={line.key}>
                  <Table.Td>
                    <Autocomplete
                      aria-label={t('entryForm.accountLine', { n: index + 1 })}
                      placeholder={t('entryForm.accountPlaceholder')}
                      data={accountOptions}
                      value={line.accountCode}
                      onChange={(value) => {
                        // The label is "1010 — Salg"; only the code is sent.
                        updateLine(line.key, { accountCode: value.split(' ')[0] ?? value });
                      }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      aria-label={t('entryForm.textLine', { n: index + 1 })}
                      value={line.description}
                      onChange={(event) => {
                        updateLine(line.key, { description: event.currentTarget.value });
                      }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      aria-label={t('entryForm.debitLine', { n: index + 1 })}
                      // inputMode rather than type="number": a number input
                      // rejects the comma a Danish keyboard produces, and
                      // silently drops what was typed.
                      inputMode="decimal"
                      ta="right"
                      value={line.debit}
                      onChange={(event) => {
                        updateLine(line.key, { debit: event.currentTarget.value });
                      }}
                      error={
                        line.debit !== '' && parseMoney(line.debit) === null
                          ? 'Ugyldigt beløb'
                          : undefined
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      aria-label={t('entryForm.creditLine', { n: index + 1 })}
                      inputMode="decimal"
                      ta="right"
                      value={line.credit}
                      onChange={(event) => {
                        updateLine(line.key, { credit: event.currentTarget.value });
                      }}
                      error={
                        line.credit !== '' && parseMoney(line.credit) === null
                          ? 'Ugyldigt beløb'
                          : undefined
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={t('entryForm.removeLine', { n: index + 1 })}
                      disabled={lines.length <= 2}
                      onClick={() => {
                        setLines((current) =>
                          current.filter((candidate) => candidate.key !== line.key),
                        );
                      }}
                    >
                      ×
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        <Group justify="space-between" align="flex-start">
          <Button
            variant="light"
            onClick={() => {
              setLines((current) => [...current, emptyLine()]);
            }}
          >
            {t('draft.addLine')}
          </Button>

          <Stack gap={4} align="flex-end">
            <Text size="sm" c="dimmed">
              {t('journal.col.debit')}{' '}
              <strong className="money-cell">{formatMoney(totals.debit)}</strong>
              {'   ·   '}
              {t('journal.col.credit')}{' '}
              <strong className="money-cell">{formatMoney(totals.credit)}</strong>
            </Text>
            {/*
              The difference is shown continuously rather than on submit. An
              unbalanced entry is the single most common mistake, and the API
              refuses it with 422 — there is no reason to make someone press
              Post to find out.
            */}
            <Text size="sm" c={totals.difference.isZero() ? 'green' : 'red'} fw={600}>
              {totals.difference.isZero()
                ? t('entryForm.inBalance')
                : t('entryForm.difference', { amount: formatMoney(totals.difference) })}
            </Text>
          </Stack>
        </Group>

        {bothSides && (
          <Alert color="orange" variant="light">
            {t('entryForm.bothSides')}
          </Alert>
        )}

        {create.isError && (
          <ErrorMessage error={create.error} title={t('entryForm.failed')} />
        )}

        <Group justify="flex-end">
          <Button onClick={handleSubmit} disabled={!canPost} loading={create.isPending}>
            {t('entryForm.post')}
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          {t('entryForm.irreversible')}
        </Text>
      </Stack>
    </Paper>
  );
}
